import { ContactUs } from "../components/ContactUs";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg, safeUrl } from "../lib/api";
import { Nav } from "../components/Nav";
import { Sparkles, Camera, Send, Heart, Users, Gift, ArrowRight, Zap, ExternalLink } from "lucide-react";

const MARQUEE = ["Make someone smile today", "Always 100% free", "Add your selfie", "Birthdays · Love · Thanks", "Send in seconds"];

function FeaturedSponsor() {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/ads/featured").then(({ data }) => setAds(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (ads.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), 6000);
    return () => clearInterval(t);
  }, [ads]);

  if (ads.length === 0) return null;
  const ad = ads[idx];

  const onClick = () => { api.post(`/ads/${ad.id}/click`).catch(() => {}); };

  return (
    <section className="max-w-7xl mx-auto px-5 py-12" data-testid="featured-sponsor">
      <div className="flex items-center gap-2 justify-center mb-6">
        <Sparkles className="w-4 h-4" />
        <span className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Featured sponsor</span>
      </div>
      <a href={safeUrl(ad.link_url)} target="_blank" rel="noopener noreferrer" onClick={onClick}
        data-testid="featured-sponsor-card"
        className="block nb-card nb-shadow-lg overflow-hidden hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[3px_3px_0px_#0A0A0A] transition-all">
        <div className="grid md:grid-cols-[auto_1fr_auto] items-center gap-6 p-6 sm:p-8 bg-[#C8B6FF]">
          {ad.image_url && (
            <img src={resolveImg(ad.image_url)} alt={ad.business_name}
              className="w-full md:w-40 h-40 object-cover rounded-xl border-2 border-[#0A0A0A]" />
          )}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/70">{ad.business_name}</div>
            <h3 className="text-2xl sm:text-3xl font-display font-bold mt-1 leading-tight">{ad.headline}</h3>
          </div>
          <span className="nb-btn bg-white px-6 py-3 whitespace-nowrap">
            {ad.cta_text || "Learn More"} <ExternalLink className="w-4 h-4" />
          </span>
        </div>
      </a>
      {ads.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4" data-testid="featured-sponsor-dots">
          {ads.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Sponsor ${i + 1}`}
              className={`h-2.5 rounded-full border border-[#0A0A0A] transition-all ${i === idx ? "w-6 bg-[#0A0A0A]" : "w-2.5 bg-white"}`} />
          ))}
        </div>
      )}
    </section>
  );
}

function Counter({ value, label, testid }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.floor(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="nb-card px-6 py-5 text-center" data-testid={testid}>
      <div className="text-3xl sm:text-4xl font-display font-bold">{n.toLocaleString()}</div>
      <div className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A] mt-1">{label}</div>
    </div>
  );
}

export default function Landing() {
  const [stats, setStats] = useState({ users: 0, greetings: 0, cities: 0 });

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const steps = [
    { icon: Gift, t: "Pick a template", d: "Hundreds of designs for every occasion." },
    { icon: Camera, t: "Add your photo", d: "Snap a selfie or upload a pic right into the card." },
    { icon: Send, t: "Send anywhere", d: "Share via text, Messenger, Instagram, X & more." },
  ];

  return (
    <div className="min-h-screen">
      <Nav />

      {/* Launch announcement */}
      <div className="bg-[#A8E6CF] border-b-2 border-[#0A0A0A] py-2 px-5 text-center text-sm font-bold" data-testid="launch-banner">
        <Sparkles className="w-4 h-4 inline mb-0.5 mr-1" /> We just launched! You're one of our first members — thanks for being here early. 💚
      </div>

      {/* Marquee */}
      <div className="bg-[#FFB5A7] border-b-2 border-[#0A0A0A] py-2 overflow-hidden">
        <div className="marquee">
          {[0, 1].map((g) => (
            <div className="marquee__group" key={g}>
              {MARQUEE.map((m, i) => (
                <span key={i} className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                  <Zap className="w-4 h-4" /> {m}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 pt-12 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 nb-card px-4 py-1.5 mb-6 bg-[#A8E6CF]">
            <Heart className="w-4 h-4" /> <span className="text-xs font-bold uppercase tracking-widest">Free forever for senders</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.05] tracking-tight">
            Send a smile.<br />
            <span className="bg-[#F9F871] px-2 nb-border inline-block rotate-[-1deg]">It's always free.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#4A4A4A] mt-6 max-w-md">
            Customize gorgeous greeting cards, drop in your own selfie, and send them to anyone — for birthdays, love notes, thank-yous and more.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/templates" data-testid="hero-create-btn" className="nb-btn bg-[#FFB5A7] px-7 py-3.5 text-lg">
              Create a greeting <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/signup" data-testid="hero-signup-btn" className="nb-btn bg-white px-7 py-3.5 text-lg">
              Sign up free
            </Link>
          </div>
        </div>

        <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <img
            src="https://images.pexels.com/photos/5428950/pexels-photo-5428950.jpeg"
            alt="Happy person with phone"
            className="w-full h-[420px] object-cover nb-card nb-shadow-lg rotate-[2deg]"
          />
          <div className="absolute -bottom-5 -left-5 nb-card px-5 py-3 bg-[#F9F871] rotate-[-3deg] animate-bounce-soft">
            <div className="flex items-center gap-2 font-bold"><Sparkles className="w-5 h-5" /> Happy Birthday! 🎉</div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-5xl mx-auto px-5 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Counter value={stats.users} label="Happy users" testid="stat-users" />
          <Counter value={stats.greetings} label="Greetings sent" testid="stat-greetings" />
          <Counter value={stats.cities} label="Cities reached" testid="stat-cities" />
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y-2 border-[#0A0A0A] py-16">
        <div className="max-w-7xl mx-auto px-5">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-center">Three taps to a smile</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {steps.map((s, i) => (
              <div key={i} className="nb-card p-7" data-testid={`step-${i}`}>
                <div className="w-14 h-14 grid place-items-center rounded-xl bg-[#A8E6CF] border-2 border-[#0A0A0A] mb-5">
                  <s.icon className="w-7 h-7" />
                </div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Step {i + 1}</div>
                <h3 className="text-xl font-display font-bold mt-1">{s.t}</h3>
                <p className="text-[#4A4A4A] mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured sponsor (rotates in placement order) */}
      <FeaturedSponsor />

      {/* Advertiser teaser */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="nb-card nb-shadow-lg p-8 sm:p-12 bg-[#FFB5A7] grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 nb-card px-3 py-1 bg-white mb-4">
              <Users className="w-4 h-4" /> <span className="text-xs font-bold uppercase tracking-widest">For businesses</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold">Put your brand in front of happy people</h2>
            <p className="text-[#0A0A0A]/80 mt-3 max-w-xl">
              Geo-targeted ads shown right when someone shares joy. Choose your reach: a city, a state, or the whole nation.
            </p>
            <p className="text-[#0A0A0A]/70 text-sm mt-3 max-w-xl" data-testid="ads-keep-it-free">
              Advertisers are what keep SendOccasions 100% free for senders — and ads only appear <strong>after</strong> a greeting has been sent, never while you're creating one.
            </p>
          </div>
          <Link to="/advertise" data-testid="advertiser-cta" className="nb-btn bg-white px-8 py-4 text-lg whitespace-nowrap">
            Advertise <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="border-t-2 border-[#0A0A0A] py-8 text-center text-sm text-[#4A4A4A]">
        <div className="flex items-center justify-center flex-wrap gap-4 mb-3">
          <Link to="/terms" data-testid="footer-terms" className="font-semibold underline">Terms of Service</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="font-semibold underline">Privacy Policy</Link>
          <Link to="/advertise" className="font-semibold underline">Advertise</Link>
          <ContactUs />
        </div>
        SendOccasions — made with <Heart className="w-4 h-4 inline" /> · Sending greetings is always free.
      </footer>
    </div>
  );
}
